import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from 'reactstrap';
import SuperAdminService from '../../service/super-admin.service';
import { useNotification } from '../../contexts/NotificationContext';
import { formatPhone } from '../../utils/phone';

const BUSINESS_TYPE_LABELS = {
  RESTAURANT: 'Restaurante',
  PIZZERIA: 'Pizzaria',
  BURGER: 'Hamburgueria',
  ICE_CREAM: 'Sorveteria',
  DISTRIBUTOR: 'Distribuidora',
  OTHER: 'Outro',
};

const ANON_PHONE = '5500000000000';

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('pt-BR');
}

function SuperAdminLeadsPage() {
  const navigate = useNavigate();
  const { notify } = useNotification();
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [anonymizingLeadId, setAnonymizingLeadId] = useState(null);
  const [customerForm, setCustomerForm] = useState({ accountId: '', customerPhone: '' });
  const [savingCustomer, setSavingCustomer] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await SuperAdminService.listLeads(search.trim() || undefined);
      setLeads(Array.isArray(data) ? data : []);
    } catch (err) {
      notify('tr', 'danger', 'Erro ao carregar leads', 'icon-alert-circle-exc');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAnonymizeLead = async (lead) => {
    if (
      !window.confirm(
        `Anonimizar o lead #${lead.id} (${lead.email})? Esta ação não pode ser desfeita.`,
      )
    ) {
      return;
    }

    setAnonymizingLeadId(lead.id);
    try {
      const result = await SuperAdminService.anonymizeLead({ id: lead.id });
      notify(
        'tr',
        'success',
        result?.alreadyAnonymized
          ? 'Lead já estava anonimizado'
          : 'Lead anonimizado',
        'icon-check-2',
      );
      load();
    } catch (err) {
      const msg = err?.response?.data?.message;
      notify(
        'tr',
        'danger',
        typeof msg === 'string' ? msg : 'Erro ao anonimizar lead',
        'icon-alert-circle-exc',
      );
    } finally {
      setAnonymizingLeadId(null);
    }
  };

  const handleAnonymizeCustomer = async (e) => {
    e.preventDefault();
    const accountId = Number(customerForm.accountId);
    const customerPhone = customerForm.customerPhone.trim();
    if (!accountId || !customerPhone) {
      notify('tr', 'warning', 'Informe a conta e o telefone', 'icon-alert-circle-exc');
      return;
    }

    if (
      !window.confirm(
        `Anonimizar pedidos da conta #${accountId} com o telefone ${customerPhone}? Esta ação não pode ser desfeita.`,
      )
    ) {
      return;
    }

    setSavingCustomer(true);
    try {
      const result = await SuperAdminService.anonymizeCustomer({
        accountId,
        customerPhone,
      });
      notify(
        'tr',
        'success',
        `${result?.ordersUpdated ?? 0} pedido(s) anonimizado(s)`,
        'icon-check-2',
      );
      setCustomerForm({ accountId: '', customerPhone: '' });
    } catch (err) {
      const msg = err?.response?.data?.message;
      notify(
        'tr',
        'danger',
        typeof msg === 'string' ? msg : 'Erro ao anonimizar cliente',
        'icon-alert-circle-exc',
      );
    } finally {
      setSavingCustomer(false);
    }
  };

  return (
    <div className="content">
      <Row className="mb-4 align-items-center">
        <Col md="8">
          <h2 className="mb-1">Leads</h2>
          <p className="text-muted mb-0">
            Contatos capturados pelo site de marketing
          </p>
        </Col>
        <Col md="4" className="text-md-right mt-3 mt-md-0">
          <Button color="secondary" outline onClick={() => navigate('/superadmin')}>
            Voltar às contas
          </Button>
        </Col>
      </Row>

      <Card className="mb-4">
        <CardHeader>
          <h4 className="card-title mb-0">Privacidade (LGPD)</h4>
        </CardHeader>
        <CardBody>
          <p className="text-muted mb-3" style={{ maxWidth: 640 }}>
            Atendimento ao titular: anonimize o lead na tabela abaixo ou os pedidos de um cliente
            pelo telefone da conta.
          </p>
          <form onSubmit={handleAnonymizeCustomer}>
            <Row className="align-items-end">
              <Col md="3">
                <FormGroup className="mb-3 mb-md-0">
                  <Label>ID da conta</Label>
                  <Input
                    type="number"
                    min="1"
                    value={customerForm.accountId}
                    onChange={(e) =>
                      setCustomerForm((current) => ({
                        ...current,
                        accountId: e.target.value,
                      }))
                    }
                    placeholder="Ex.: 12"
                  />
                </FormGroup>
              </Col>
              <Col md="5">
                <FormGroup className="mb-3 mb-md-0">
                  <Label>Telefone do cliente</Label>
                  <Input
                    value={customerForm.customerPhone}
                    onChange={(e) =>
                      setCustomerForm((current) => ({
                        ...current,
                        customerPhone: e.target.value,
                      }))
                    }
                    placeholder="(31) 99999-9999"
                  />
                </FormGroup>
              </Col>
              <Col md="4">
                <Button color="warning" type="submit" disabled={savingCustomer}>
                  {savingCustomer ? 'Anonimizando…' : 'Anonimizar cliente'}
                </Button>
              </Col>
            </Row>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <Row className="align-items-center">
            <Col md="6">
              <h4 className="card-title mb-md-0">Lista de leads</h4>
            </Col>
            <Col md="6" className="mt-3 mt-md-0">
              <Input
                placeholder="Buscar por nome, e-mail, telefone ou estabelecimento..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load()}
              />
            </Col>
          </Row>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <Table hover responsive className="tablesorter mb-0">
              <thead className="text-primary">
                <tr>
                  <th>ID</th>
                  <th>Nome</th>
                  <th>Contato</th>
                  <th>Estabelecimento</th>
                  <th>Tipo</th>
                  <th>Local</th>
                  <th>Mensagem</th>
                  <th>Data</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center text-muted py-4">
                      Nenhum lead encontrado
                    </td>
                  </tr>
                )}
                {leads.map((lead) => {
                  const anonymized = lead.phone === ANON_PHONE;
                  return (
                    <tr key={lead.id}>
                      <td>{lead.id}</td>
                      <td>
                        <strong>{lead.name}</strong>
                      </td>
                      <td>
                        {lead.email}
                        <br />
                        <small className="text-muted">
                          {lead.phone ? formatPhone(lead.phone) : '—'}
                        </small>
                      </td>
                      <td>{lead.businessName}</td>
                      <td>
                        {BUSINESS_TYPE_LABELS[lead.businessType] || lead.businessType}
                      </td>
                      <td>
                        {[lead.city, lead.region].filter(Boolean).join(' / ') || '—'}
                        {lead.country ? (
                          <>
                            <br />
                            <small className="text-muted">{lead.country}</small>
                          </>
                        ) : null}
                      </td>
                      <td style={{ maxWidth: 220 }}>
                        <span className="text-muted" title={lead.message || ''}>
                          {lead.message
                            ? lead.message.length > 80
                              ? `${lead.message.slice(0, 80)}…`
                              : lead.message
                            : '—'}
                        </span>
                      </td>
                      <td>{formatDateTime(lead.createdAt)}</td>
                      <td className="text-right">
                        <Button
                          color="warning"
                          size="sm"
                          outline
                          disabled={anonymized || anonymizingLeadId === lead.id}
                          onClick={() => handleAnonymizeLead(lead)}
                        >
                          {anonymized
                            ? 'Anonimizado'
                            : anonymizingLeadId === lead.id
                              ? '…'
                              : 'Anonimizar'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export default SuperAdminLeadsPage;
